import { AudioLines, Mail, MailOpen, MessageSquare, MessageSquareReply, PhoneIncoming, PhoneOutgoing, Sparkles, StickyNote } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";
import { errText, frDate } from "@/components/pipeline/utils";
import { RichText } from "@/components/pipeline/RichText";
import { CallTranscript } from "@/components/pipeline/CallTranscript";

const KIND = {
  comment: { icon: StickyNote, label: "Commentaire" }, email_in: { icon: MailOpen, label: "E-mail reçu" }, email_out: { icon: Mail, label: "E-mail envoyé" },
  sms_in: { icon: MessageSquareReply, label: "SMS reçu" }, sms_out: { icon: MessageSquare, label: "SMS envoyé" },
  call_in: { icon: PhoneIncoming, label: "Appel Ringover" }, call_out: { icon: PhoneOutgoing, label: "Appel Ringover" },
};

// Journaux d'appel écrits automatiquement dans noCRM : déjà affichés comme appels Ringover
const AUTO_CALL_LOG = /^\s*(<[^>]+>\s*)*(📞\s*)?Appel (entrant|sortant) Ringover/;
export const KIND_LABEL = Object.fromEntries(Object.entries(KIND).map(([k, v]) => [k, v.label]));
export const KIND_ICON = Object.fromEntries(Object.entries(KIND).map(([k, v]) => [k, v.icon]));
// Commentaires (hors journaux d'appel automatiques) + e-mails, SMS et appels, du plus récent au plus ancien
export const timelineItems = (comments, communications) => [
  ...comments.filter((c) => (c.content || "").trim() && !AUTO_CALL_LOG.test(c.content)).map((c) => ({ kind: "comment", id: `c-${c.id}`, title: `${c.user || "Commentaire"}${c.pending ? " · copie noCRM en attente" : ""}`, at: c.at, excerpt: c.content })),
  ...communications.map((c) => ({ ...c, id: `m-${c.kind}-${c.id}` })),
].sort((a, b) => (b.at || "").localeCompare(a.at || ""));

export const CallAi = ({ item, onApplyDate, remindDate, aiUrl }) => {
  const [ai, setAi] = useState(item.ai_summary ? item : null);
  const [busy, setBusy] = useState(false);
  // résumé automatique côté serveur pour les appels de plus de 30 s ; ici, bouton manuel pour les autres
  const run = async () => {
    setBusy(true);
    try { setAi((await api.post(aiUrl || `/pipeline/calls/${item.call_id}/ai`)).data); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  if (!item.transcript && !item.excerpt) return null;
  if (!ai) return <button type="button" className="pl-link" onClick={() => run()} disabled={busy} data-testid={`call-ai-btn-${item.call_id}`}><Sparkles size={13} /> {busy ? "Résumé en cours…" : "Résumer l'appel avec l'IA"}</button>;
  return (
    <div className="pl-call-ai" data-testid={`call-ai-${item.call_id}`}>
      <strong className="pl-call-ai-title"><Sparkles size={12} /> Résumé IA de l'appel</strong>
      <p>{ai.ai_summary}</p>
      {ai.ai_next_action && <p><strong>Prochaine action :</strong> {ai.ai_next_action}</p>}
      {ai.ai_next_date && ai.ai_next_date === remindDate && <small className="pl-muted">Relance déjà planifiée au {frDate(ai.ai_next_date)}.</small>}
      {onApplyDate && ai.ai_next_date && ai.ai_next_date !== remindDate && <button type="button" className="secondary-button" onClick={() => onApplyDate(ai.ai_next_date)} data-testid={`call-ai-apply-${item.call_id}`}>Planifier la relance au {frDate(ai.ai_next_date)}</button>}
    </div>
  );
};

export const CallBody = ({ item, base, onApplyDate, remindDate }) => {
  const [c, setC] = useState(item);
  const [busy, setBusy] = useState(false);
  const url = base || `/pipeline/calls/${item.call_id}`;
  const transcribe = async () => {
    setBusy(true);
    try { setC({ ...c, ...(await api.post(`${url}/transcribe`)).data }); toast.success("Appel transcrit par l'IA."); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <CallAi key={c.transcript ? "t" : "n"} item={c} aiUrl={`${url}/ai`} onApplyDate={onApplyDate} remindDate={remindDate} />
      {c.excerpt && !c.ai_summary && <RichText className="pl-tl-text" text={c.excerpt} />}
      {c.record_url && <audio className="pl-audio" controls preload="metadata" src={c.record_url} data-testid={`call-recording-player-${c.call_id}`} />}
      {c.transcript && <CallTranscript item={c} />}
      {c.record_url && !c.transcript && <button type="button" className="pl-link" disabled={busy} onClick={transcribe} data-testid={`call-transcribe-btn-${c.call_id}`}><AudioLines size={13} /> {busy ? "Transcription en cours (30 s environ)…" : "Transcrire l'appel avec l'IA"}</button>}
      {c.transcript_source === "ia" && <small className="pl-muted">Transcription réalisée par l'IA</small>}
    </>
  );
};


